import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, imgSrc, catLabel, fmtMoney, bannableLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { TrustBadge } from "@/components/TrustBadge";
import { toast } from "sonner";
import { ArrowLeft, Ruler, Sparkles, Heart, ShieldCheck, ImageOff, ShieldQuestion, Ban } from "lucide-react";

const SpecRow = ({ label, value }) =>
  value ? (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  ) : null;

export default function ModelProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/models/${id}`)
      .then(({ data }) => setModel(data))
      .catch(() => toast.error("Model not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="page-container grid gap-8 py-10 lg:grid-cols-5">
        <Skeleton className="aspect-[3/4] rounded-2xl lg:col-span-3" />
        <div className="space-y-4 lg:col-span-2">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="page-container py-20 text-center">
        <p className="font-display text-2xl">Model not found</p>
        <Button variant="outline" className="mt-4 rounded-xl" onClick={() => navigate("/explore")}>Back to explore</Button>
      </div>
    );
  }

  const images = model.images || [];
  const current = images[activeImg];
  const isOwner = user && model.owner_user_id === user.id;
  const hiddenFromMarketplace = model.onboarding && !model.onboarding.complete;
  const onBook = () => {
    if (!user) {
      toast.info("Sign in as a client to book models");
      navigate("/auth");
      return;
    }
    if (user.role !== "client") {
      toast.error("Only client accounts can book models");
      return;
    }
    navigate(`/book/${model.id}`);
  };

  return (
    <div className="page-container py-8 sm:py-12">
      <button data-testid="model-profile-back-button" onClick={() => navigate(-1)} className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground transition-[color] hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {isOwner && hiddenFromMarketplace && (
        <div data-testid="model-profile-hidden-notice" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-[hsl(43,74%,70%)] bg-[hsl(43,74%,95%)] p-4 text-sm">
          <p className="text-[hsl(43,60%,22%)]">
            <span className="font-semibold">Only you can see this page.</span> This profile is hidden from the
            marketplace until onboarding (ID scan + photo dataset + AI metrics) is complete.
          </p>
          <Button data-testid="model-profile-onboarding-button" size="sm" className="rounded-xl" onClick={() => navigate(`/models/${model.id}/onboarding`)}>
            Continue onboarding
          </Button>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-5">
        {/* GALLERY */}
        <div data-testid="model-profile-gallery" className="space-y-3 lg:col-span-3">
          <div className="overflow-hidden rounded-2xl border bg-muted shadow-[0_10px_40px_rgba(30,26,22,0.08)]">
            {current ? (
              <img src={imgSrc(current)} alt={model.name} className="aspect-[3/4] w-full object-cover" />
            ) : (
              <div className="flex aspect-[3/4] items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <ImageOff className="mx-auto h-10 w-10" />
                  <p className="mt-2 text-sm">No photos yet</p>
                </div>
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {images.map((img, i) => (
                <button
                  key={img.id}
                  data-testid="model-profile-thumbnail"
                  onClick={() => setActiveImg(i)}
                  className={`h-20 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-[border-color] ${
                    i === activeImg ? "border-[hsl(174,38%,34%)]" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={imgSrc(img)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* DETAILS RAIL */}
        <div className="space-y-5 lg:col-span-2">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-4xl tracking-[-0.02em]">{model.name}</h1>
              <TrustBadge trust={model.trust} />
            </div>
            {model.gender && <p className="mt-1 text-sm capitalize text-muted-foreground">{model.gender}</p>}
          </div>
          {model.trust === "unverified" && (
            <div data-testid="model-profile-unverified-notice" className="flex items-start gap-2.5 rounded-xl border bg-muted p-3 text-xs text-muted-foreground">
              <ShieldQuestion className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                This independent model hasn't completed identity verification yet. You can still send a booking
                request, but we recommend prioritising <span className="font-semibold text-foreground">Verified</span> or{" "}
                <span className="font-semibold text-foreground">Agency-represented</span> looks.
              </span>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {(model.categories || []).map((c) => (
              <Badge key={c} variant="secondary" className="rounded-full text-[10px] uppercase tracking-wide">{catLabel(c)}</Badge>
            ))}
          </div>
          {model.bio && <p className="text-sm leading-relaxed text-muted-foreground">{model.bio}</p>}

          <Card className="rounded-xl">
            <CardContent className="p-4">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Ruler className="h-3.5 w-3.5" /> Features & measurements
              </p>
              <SpecRow label="Height" value={model.features?.height_cm ? `${model.features.height_cm} cm` : null} />
              <SpecRow label="Hair" value={model.features?.hair_color} />
              <SpecRow label="Eyes" value={model.features?.eye_color} />
              <SpecRow label="Skin tone" value={model.features?.skin_tone} />
              <Separator className="my-2" />
              <SpecRow label="Bust" value={model.measurements?.bust_cm ? `${model.measurements.bust_cm} cm` : null} />
              <SpecRow label="Waist" value={model.measurements?.waist_cm ? `${model.measurements.waist_cm} cm` : null} />
              <SpecRow label="Hips" value={model.measurements?.hips_cm ? `${model.measurements.hips_cm} cm` : null} />
              <SpecRow label="Shoe (EU)" value={model.measurements?.shoe_size_eu} />
              <SpecRow label="Dress size" value={model.measurements?.dress_size} />
            </CardContent>
          </Card>

          {model.skills?.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" /> Skills
              </p>
              <div className="flex flex-wrap gap-1.5">
                {model.skills.map((s) => (
                  <Badge key={s} variant="outline" className="rounded-full">{s}</Badge>
                ))}
              </div>
            </div>
          )}

          {model.interests?.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Heart className="h-3.5 w-3.5" /> Interests
              </p>
              <div className="flex flex-wrap gap-1.5">
                {model.interests.map((s) => (
                  <Badge key={s} variant="outline" className="rounded-full">{s}</Badge>
                ))}
              </div>
            </div>
          )}

          {model.banned_categories?.length > 0 && (
            <div data-testid="model-profile-banned-categories">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Ban className="h-3.5 w-3.5 text-destructive" /> Will not model
              </p>
              <div className="flex flex-wrap gap-1.5">
                {model.banned_categories.map((s) => (
                  <Badge key={s} variant="outline" className="rounded-full border-[hsl(6,62%,72%)] bg-[hsl(6,62%,95%)] text-[hsl(6,55%,25%)]">
                    {bannableLabel(s)}
                  </Badge>
                ))}
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Binding opt-outs — jobs touching these categories are automatically rejected at screening.
              </p>
            </div>
          )}

          <Card className="rounded-xl border-2 border-[hsl(174,38%,70%)]">
            <CardContent className="space-y-3 p-4">
              {(model.rate_min || model.rate_max) && (
                <div className="flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Typical licence range</span>
                  <span className="font-display text-xl">{fmtMoney(model.rate_min)} – {fmtMoney(model.rate_max)}</span>
                </div>
              )}
              <Button
                data-testid="model-profile-request-to-book-button"
                onClick={onBook}
                className="w-full rounded-xl shadow-[0_10px_25px_rgba(30,26,22,0.12)] transition-[box-shadow] hover:shadow-[0_14px_35px_rgba(30,26,22,0.16)]"
                size="lg"
              >
                Request to book
              </Button>
              <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-[hsl(174,38%,34%)]" />
                AI-screened job · model consent required · paid upfront
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
